import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import Spinner from "./ui/Spinner";
import Button from "./ui/Button";
import { useViewer, type ViewerOrganization } from "../services/viewer";

/**
 * Puts somebody in their organization without asking them to name it.
 *
 * **The problem.** The post-login Action resolves the tenant claim from the
 * organization's metadata when a login carries organization context, and from the
 * person's own `app_metadata` otherwise. An organization member who signs in without
 * it therefore lands in their personal tenant and sees an empty People page — which
 * is what happened to the first person invited through the dashboard. Nothing was
 * misconfigured; the login simply did not say which organization.
 *
 * **Why not Auth0's own prompt.** It asks the person to type the organization's slug,
 * which nobody knows. Matching on an email domain needs a domain registered against
 * each customer's organization — another manual onboarding step, and removing those
 * is the whole point.
 *
 * So: their membership already knows. One organization signs them straight back in
 * with it; several ask, using real display names; none leaves them alone, which is
 * every family user.
 */
export default function OrganizationGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const { loginWithRedirect } = useAuth0();
  const { data: viewer, isPending, isError } = useViewer();

  const organizations = viewer?.organizations ?? [];
  const needsOrganization =
    !!viewer && viewer.org_context !== true && organizations.length > 0;

  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!needsOrganization || organizations.length !== 1) return;
    if (alreadyTried(organizations[0].id)) return;

    // Remembered BEFORE the redirect, not after: the page is about to be
    // replaced, so there is no "after" here to record it in.
    remember(organizations[0].id);
    setRedirecting(true);

    loginWithRedirect({
      authorizationParams: { organization: organizations[0].id },
    });
  }, [needsOrganization, organizations, loginWithRedirect]);

  // The viewer gates everything below, and a failure to read it is handled where
  // it already is -- this component only decides about organizations.
  if (isPending || isError || !viewer) return <>{children}</>;

  if (!needsOrganization) return <>{children}</>;

  if (organizations.length === 1) {
    // Either mid-redirect, or the redirect was already tried and did not take.
    return redirecting || alreadyTried(organizations[0].id) ? (
      <Stuck organization={organizations[0]} onRetry={() => forget()} />
    ) : (
      <Working />
    );
  }

  return (
    <Chooser
      organizations={organizations}
      onChoose={(id) =>
        loginWithRedirect({ authorizationParams: { organization: id } })
      }
    />
  );
}

/**
 * Whether we have already sent this browser through sign-in for an organization.
 *
 * **The loop this prevents is the whole reason it exists.** If the Action does not
 * put the organization on the token -- its metadata is missing the tenant id, the
 * Action errors, the organization is misconfigured -- then the login comes back
 * exactly as it left and the condition that triggered it is still true. Without a
 * record, that is an infinite redirect, and the person never reaches a screen that
 * could tell them why.
 *
 * `sessionStorage`, not `localStorage`: one attempt per browser session is the right
 * granularity. Signing out and back in should try again. Wrapped because storage
 * throws in a private window, and a redirect loop is a worse failure than a lost
 * flag -- so a browser that cannot remember is treated as having already tried.
 */
const KEY = "cts.organization-signin-attempted";

function alreadyTried(organizationId: string): boolean {
  try {
    return window.sessionStorage.getItem(KEY) === organizationId;
  } catch {
    return true;
  }
}

function remember(organizationId: string): void {
  try {
    window.sessionStorage.setItem(KEY, organizationId);
  } catch {
    // Nothing to do. alreadyTried answers true when storage is unavailable, so
    // the redirect is not attempted at all rather than attempted repeatedly.
  }
}

function forget(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* see remember */
  }
}

function Working() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-white">
      <Spinner size={64} />
      <p className="text-ink-3 text-sm">Signing you in to your organization…</p>
    </div>
  );
}

/**
 * Shown when the redirect did not produce organization context.
 *
 * Says what is actually true rather than retrying silently: the sign-in happened
 * and did not take. The retry clears the record and reloads, so a transient failure
 * is one click away, and a persistent one stops here instead of looping.
 */
function Stuck({
  organization,
  onRetry,
}: {
  organization: ViewerOrganization;
  onRetry: () => void;
}) {
  const name = organization.display_name || "your organization";

  return (
    <div className="min-h-screen flex items-center justify-center p-6 text-white">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-xl font-semibold">
          We couldn't sign you in to {name}
        </h1>
        <p className="text-ink-3 text-sm">
          You're signed in, but not as a member of {name}, so you won't see its
          sensors or people. This usually means the organization needs its setup
          finished.
        </p>
        <Button
          onClick={() => {
            onRetry();
            window.location.reload();
          }}
        >
          Try again
        </Button>
      </div>
    </div>
  );
}

/**
 * More than one organization, so it has to be asked.
 *
 * Display names, never Auth0's slug -- the slug is what its own prompt asks for and
 * the reason this component exists.
 */
function Chooser({
  organizations,
  onChoose,
}: {
  organizations: ViewerOrganization[];
  onChoose: (id: string) => void;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 text-white">
      <div className="w-full max-w-md space-y-4">
        <h1 className="text-xl font-semibold text-center">
          Which organization?
        </h1>
        <div className="space-y-2">
          {organizations.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onChoose(o.id)}
              className="w-full rounded-control border border-white/15 bg-white/5 px-4 py-3 text-left hover:bg-white/10 transition-colors"
            >
              {o.display_name || o.id}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
